/**
 * Define feature flag here as constants.
 *
 * export const KB_ENHANCEMENTS = 'jsd.px.kb-enhancements';
 *
 * Use them in react components.
 * import { KB_ENHANCEMENTS } from '@helpcenter/feature-flags';
 * import { FeatureFlag } from 'view/common/feature-flag';
 *
 * <FeatureFlag name={KB_ENHANCEMENTS}>
 *  I will be shown in true :-)
 * </FeatureFlag>
 *
 * ========== NOTE ===================
 * BE CAREFUL ADDING FEATURE FLAGS, IF YOU RENAME THEM IT WILL CHANGE THE ANALYTIC CONTEXT
 * NAME AND NO LONGER SEND THE SAME CONTEXT ATTRIBUTE NAME.
 * ===================================
 */

import FeatureGates from '@atlaskit/feature-gate-js-client';
// Be careful when using these constants directly. In most cases, you should use the function.
export const RICH_TEXT_AREA_FIELD = 'jsd-use-adf-default-value-for-textarea';
export const ENHANCED_RENDER_EXPERIENCE_FOR_KNOWLEDGE_ARTICLES =
	'jsm-enhanced-render-exp-for-knowledge-articles';

const ENABLE_KB_ARTICLES_OPEN_IN_SAME_TAB = 'make-kb-articles-open-in-same-tab';

export const UNIFIED_HELP_ADD_FILTER_FOR_CUSTOMER_ACCOUNTS =
	'unified-help-add-filter-for-customer-accounts';

const JCSM_CS_MASTER_FLAG = 'jcs_master_flag';
const CSM_ACCESS_CONTROL_SPLIT = 'csm_access_control_split_take2';
const CSM_PREMIUM_SLA_DISABLED_FLAG = 'jsm_csm_premium_sla_config';
const AI_WIDGET_TEAM25 = 'berry-5094-ai-widget-support-website-team25';
const CSM_CONTACT_US_SECTION = 'jcs_contact_us_section';
const SEND_RAW_HELP_CENTER_TYPE_IN_ANALYTICS = 'help-center-fix-type-in-analytics';

export const JSM_VIRTUAL_AGENT_ANONYMOUS_ACCESS = 'anonymous_virtual_agent_access';

export const ENABLE_REACT_18_CREATEROOT = 'enable_react-18';
export const DONT_USE_HYDRATE_SSR = 'dont_use_hydrate_in_portal_ssr';

const FIX_RELAY_ID_CONFLICT_IN_HELP_CENTER = 'fix_relay_id_conflict_in_help_center';
const FIX_RELAY_ID_CONFLICT_FOR_HELP_OBJECT_STORE = 'fix_relay_id_conflict_for_help_object_store';

const TUNING_RETRY_MECHANISM_FOR_MODELS_ENDPOINT = 'tuning_model_retries';
const FILTER_PREMIUM_SLA_ERRORS = 'filter_premium_sla_errors';
const CUSTOMISATION_BUTTON_CHANGE_BOARDING = 'enable_customisation_button_change_boarding';
const PROJECT_MAPPING_ENABLED_IN_MULTI_HC = 'enable_project_mapping_for_help_center';
const ENABLE_CROSS_SITE_CONFLUENCE = 'enable-cross-site-confluence';
const PROFORMA_TTVC_UPDATE_PACKAGE_ENABLED = 'measure_proforma_ttvc_impact';
const REMOVE_CREATE_REQUEST_REDUNDANT_MODELS = 'jsm-create-request-redundant-models-fe';
const CONSUME_LOOSELY_LAZY_MANIFEST_IN_SSR = 'consume_loosely_lazy_manifest_in_ssr';
const DEFAULT_HC_USING_GQL_BRANDING = 'default_hc_using_gql_branding';
const PROJECT_MAPPING_CHANGE_BOARDING = 'project_mapping_change_boarding';
export const HELP_CENTER_REFRESH_FLAG = 'enable_increased_flexibility'; // TODO: using same key as increased flexibility for bundle release
const ENABLE_FORGE_APPS_IN_JSM_PHASE_1 = 'enable_forge_apps_in_jsm_layout_builder_phase_1';
export const EMAIL_VERIFIED_SIGNAL_ON_CUSTOMERS_PAGE =
	'sptn-1697-email-verified-signal-on-customers-page';
const HELP_CENTER_CUSTOM_PAGES = 'help_center_custom_pages_ui';
export const ENABLE_INCREASED_FLEXIBILITY = 'enable_increased_flexibility';
export const ENABLE_FOLLOWUP_QUESTIONS_ON_PORTAL =
	'enable_followup_questions_on_omni_channel_portal';
const REQUEST_CREATE_DROPDOWN_INITIAL_OPTIONS_CALL = 'request_create_dropdown_initial_options_call';
const HIDE_KB_COLUMN_PROJECT_BREAKDOWN = 'hc_vsa_settings_hide_kb_article_column';
const VSA_SETTINGS_PERFORMANCE_IMPROVEMENTS = 'hc_vsa_settings_performance_improvements';
const COLOR_DESIGN_TOKEN_ADOPTION = 'add_color_design_token_support_into_help_center';
const TOTAL_ARTICLE_COUNT = 'hc_show_kb_total_article_count';
const VSA_ADOPTION_TOUCHPOINT_HELP_CENTER = 'vsa-adoption-touchpoint-help-center';
const HIDE_VSA_ENDPOINTS = 'hide_vsa_endpoints';
const VSA_ADOPTION_HEADER_CHAT_BUTTON = 'vsa-adoption-header-chat-button';
const VSA_ADOPTION_CHAT_SIDE_BAR = 'vsa-adoption-portal-chat-side-bar';
const ENABLE_HUBS_IN_HELP_CENTER = 'enable-hubs-in-help-center';
const ENABLE_MULTI_SELECT_CF_UIM_SUPPORT = 'ditto_uim_jsm_multi_select_support';
const CORTOPV2_EXPERIMENT = 'jsmgrowth-177_jsm_cortopv2';
const CORTOPV2_EXPERIMENT_TG = 'jsmgrowth-177_jsm_cortopv2_tg';
const JSM_FORGE_PORTAL_FIX_UNDEFINED_EXTENSION_ERROR =
	'jsm_forge_portal_fix_undefined_extension_error';
const JSM_PORTAL_ROVO_INTEGRATION = 'jsm_portal_rovo_integration';
const JSM_DATEPICKER_FIELD_SUPPORT = 'ditto_uim_jsm_datepicker_field_support';
const JSM_PORTAL_KEEP_REDIRECTION_DESTINATION =
	'jsdcloud_15510_portal_keep_redirection_destination';
const JSM_SURVEYS_CORE_FEATURE = 'jsm_surveys_core_feature';
const JSM_SURVEYS_REMOVE_HIDDEN_SECTION_RESPONSES = 'phx-3898_remove_hidden_section_responses';
const JSM_SURVEYS_ADS_BORDER_RADIUS = 'phx_3949_ads_border_radius_update';

const ROVO_AGENTS_TOOLS_CONFIG_M2 = 'rovo_agents_tools_config_m2';

const REQUEST_LIST_INFINITE_SCROLL = 'portal-6922-request-list-jsis-pagination';
const APPROVAL_LIST_INFINITE_SCROLL = 'portal-approval-list-jsis-pagination';
const EMPTY_STRING_FILTER_ON_FIRST_REQ_LIST_LOAD = 'empty_string_filter_on_first_req_list_load';

export const isEmailVerifiedSignalEnabled = () => {
	return FeatureGates.checkGate(EMAIL_VERIFIED_SIGNAL_ON_CUSTOMERS_PAGE);
};

// TODO: remove this code, we are marking this true given timeline for LD Deprecation
export const isWrmOptimizationsEnabled = () => true;

export const isRichTextAreaEnabled = () => FeatureGates.checkGate(RICH_TEXT_AREA_FIELD);

export const isEnhanceRenderExperienceForKnowledgeArticlesEnabled = () =>
	FeatureGates.checkGate(ENHANCED_RENDER_EXPERIENCE_FOR_KNOWLEDGE_ARTICLES);

export const islooselyLazyJsErrorsToSentryEnabled = () =>
	FeatureGates.checkGate('throw_lazy_error_boundary_js_errors_to_sentry');

/*
 * Cleaning up the LD feature flag 'jsm-portal-custom-theming'
 * https://app.launchdarkly.com/jira/production/features/jsm-portal-custom-theming
 * Custom themes were not rolled out.
 *
 * */
export const isCustomThemingEnabled = () => false;

export const isProjectMappingInHelpCenterEnabled = () => {
	return FeatureGates.checkGate(PROJECT_MAPPING_ENABLED_IN_MULTI_HC);
};

export const isUnifiedHelpAddFilterForCustomerAccounts = () => {
	return FeatureGates.checkGate(UNIFIED_HELP_ADD_FILTER_FOR_CUSTOMER_ACCOUNTS);
};

export const isFixRelayIdConflictInHelpCenter = () => {
	return FeatureGates.checkGate(FIX_RELAY_ID_CONFLICT_IN_HELP_CENTER);
};

export const isFixRelayIdConflictForHelpObjectStoreEnabled = () => {
	return FeatureGates.checkGate(FIX_RELAY_ID_CONFLICT_FOR_HELP_OBJECT_STORE);
};

// JSM Boysenberry feature flags
export const isJsmCsMasterFlagEnabled = () => {
	return FeatureGates.checkGate(JCSM_CS_MASTER_FLAG);
};

export const isSendRawHelpCenterTypeInAnalyticsEnabled = () => {
	return FeatureGates.checkGate(SEND_RAW_HELP_CENTER_TYPE_IN_ANALYTICS);
};

export const isCsmAccessControlSplitEnabled = () => {
	return FeatureGates.checkGate(CSM_ACCESS_CONTROL_SPLIT);
};

export const isAiWidgetTeam25Enabled = () => {
	return FeatureGates.checkGate(AI_WIDGET_TEAM25);
};

// CSM Premium SLA disabled flag
export const isCsmPremiumSlaDisabledFlagEnabled = () => {
	return FeatureGates.checkGate(CSM_PREMIUM_SLA_DISABLED_FLAG);
};

// CSM Kalari feature flags
export const isUpdateArticlesLinkCsmEnabled = () => {
	return FeatureGates.checkGate('update_articles_links_csm');
};

export const isJcsprojectTypeM3Enabled = () => {
	return FeatureGates.checkGate('jcs_project_type_m3');
};

export const isCsmAppification = () => {
	return FeatureGates.checkGate('csm_appification_standalone_navigation');
};

export const isCsmContactUsSectionEnabled = () => {
	return FeatureGates.checkGate(CSM_CONTACT_US_SECTION);
};

export const isCrossSiteConnieConnectionEnabled = () => {
	return FeatureGates.checkGate(ENABLE_CROSS_SITE_CONFLUENCE);
};

export const isKbArticlesSameTabRedirectionEnabled = () => {
	return FeatureGates.checkGate(ENABLE_KB_ARTICLES_OPEN_IN_SAME_TAB);
};

export const isUseReact18CreateRootEnabled = () => {
	return FeatureGates.checkGate(ENABLE_REACT_18_CREATEROOT);
};

export const isReactHydrateDisabledInSSR = () => {
	return FeatureGates.checkGate(DONT_USE_HYDRATE_SSR);
};

export const isVirtualAgentHeaderButtonEnabled = () => {
	return FeatureGates.checkGate(VSA_ADOPTION_TOUCHPOINT_HELP_CENTER);
};

export const isCustomiseDropDownChangeBoardingEnabled = () => {
	return FeatureGates.checkGate(CUSTOMISATION_BUTTON_CHANGE_BOARDING);
};

export const isPremiumSLAErrorFilterEnabled = () => {
	return FeatureGates.checkGate(FILTER_PREMIUM_SLA_ERRORS);
};

export const isRetryMechanismTuningEnabled = () => {
	return FeatureGates.checkGate(TUNING_RETRY_MECHANISM_FOR_MODELS_ENDPOINT);
};

export const isProformaTTVCUpdateMeasureEnabled = () => {
	return FeatureGates.checkGate(PROFORMA_TTVC_UPDATE_PACKAGE_ENABLED);
};

export const isRemoveRedundantModelsCreateRequest = () => {
	return FeatureGates.checkGate(REMOVE_CREATE_REQUEST_REDUNDANT_MODELS);
};

export const isVAOnHelpCenterGAEnabled = () => {
	return FeatureGates.checkGate('va_on_help_center_ga');
};

export const isConsumeLooselyLazyManifestInSSREnabled = () => {
	return FeatureGates.checkGate(CONSUME_LOOSELY_LAZY_MANIFEST_IN_SSR);
};

export const isMigrateToAnalyticsBridgeEnabled = () => {
	return FeatureGates.checkGate('jsm-portal-migrate-to-analytics-bridge');
};

export const isDefaultHCUsingGqlBrandingCalls = () => {
	return FeatureGates.checkGate(DEFAULT_HC_USING_GQL_BRANDING);
};

export const isProjectMappingChangeBoardingEnabled = () => {
	return FeatureGates.checkGate(PROJECT_MAPPING_CHANGE_BOARDING);
};

export const isUseHelpCenterRefresh = () => {
	return FeatureGates.checkGate(HELP_CENTER_REFRESH_FLAG);
};

export const isDraftArticleSuggestionsEnabled = () => {
	return FeatureGates.checkGate('ai-answer-using-draft-article');
};

// Ditto FCF (start)

export const isForgeCustomFieldsOnPortalViewEnabled = () =>
	FeatureGates.checkGate('forge_custom_fields_on_jsm_portal_view');

export const isOverridingFragmentsAnalyticsPropEnabled = () =>
	FeatureGates.checkGate('is_overriding_fragments_analytics_prop');

// Ditto FCF (end)

export const isForgeAppsInJSMPhase1Enabled = () =>
	FeatureGates.checkGate(ENABLE_FORGE_APPS_IN_JSM_PHASE_1);

// Ditto UIM (start)

export const isUiModificationsSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_-_jsm_support_main');

export const isTextFieldSupportEnabled = () => {
	return FeatureGates.checkGate('uim_jsm_textfield_support');
};

export const isNumberFieldSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_number_field_support');
};

export const isDateTimeSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_datetime_field_support');
};

export const isDueDateSupportEnabled = () => {
	return FeatureGates.checkGate('uim_jsm_duedate_support');
};

export const isTextAreaSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_textarea_support');
};

export const isCascadingSelectSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_cascading_select_support');
};

export const isLabelsSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_labels_support');
};

export const isUserPickerSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_user_picker_support');
};

export const cascadingSelectDefaultValueSupportEnabled = () => {
	return FeatureGates.checkGate('ditto_uim_jsm_cascading_default_value_support');
};

// Ditto UIM (end)

export const isFollowupEnabledForPortal = () => {
	return FeatureGates.checkGate(ENABLE_FOLLOWUP_QUESTIONS_ON_PORTAL);
};

export const isAiAnswer24HourTimeoutEnabled = () => {
	return FeatureGates.checkGate('ai_answer_default_timeout_to_24_hours');
};

export const isCustomPagesUiEnabled = () => {
	return FeatureGates.checkGate(HELP_CENTER_CUSTOM_PAGES);
};

export const isIncreasedFlexibilityEnabled = () => {
	return FeatureGates.checkGate(ENABLE_INCREASED_FLEXIBILITY);
};

export const isElementRendererSplitEnabled = () => {
	return FeatureGates.checkGate('enable_element_renderer_split');
};

export const isOmnichannelEnabledForHC = () => {
	return FeatureGates.checkGate('enable_omni_channel_for_helpcenter');
};

export const isClearRequestFormOnUnmount = () => {
	return FeatureGates.checkGate('clear_request_form_on_unmount');
};

export const isItsmIssueRenamingEnabled = () => {
	return FeatureGates.checkGate('itsm-issue-renaming');
};

export const isItsmProjectRenamingEnabled = () => {
	return FeatureGates.checkGate('itsm-project-renaming');
};

export const isOmnichannelGreetingAPIEnabled = () => {
	return FeatureGates.checkGate('jsm_chat_orchestrator_greeting_api_enabled');
};

export const isIsomorphicDompurifyLibUsed = () => {
	return FeatureGates.checkGate('isomorphic-dompurify-enabled');
};

export const isOmnichannelSubscriptionErrorpageEnabled = () => {
	return FeatureGates.checkGate('enable_omnichannel_subscription_error_page');
};

export const isOmnichannelAccessibilityFixesEnabled = () => {
	return FeatureGates.checkGate('helpcenter_omnichannel_accessibility_fixes');
};

export const isRequestCreateSkipDropdownInitialOptionsCallEnabled = () => {
	return FeatureGates.checkGate(REQUEST_CREATE_DROPDOWN_INITIAL_OPTIONS_CALL);
};

export const isHideKbColumnEnabled = () => {
	return FeatureGates.checkGate(HIDE_KB_COLUMN_PROJECT_BREAKDOWN);
};

export const isVsaSettingsPerformanceImprovementsEnabled = () => {
	return FeatureGates.checkGate(VSA_SETTINGS_PERFORMANCE_IMPROVEMENTS);
};

export const isTotalArticleCountEnabled = () => {
	return FeatureGates.checkGate(TOTAL_ARTICLE_COUNT);
};

export const isHideVsaEndpointsEnabled = () => {
	return FeatureGates.checkGate(HIDE_VSA_ENDPOINTS);
};

export const isVsaAdoptionHeaderChatButtonEnabled = () => {
	return FeatureGates.checkGate(VSA_ADOPTION_HEADER_CHAT_BUTTON);
};

export const isColorDesignTokenEnabled = () => {
	return FeatureGates.checkGate(COLOR_DESIGN_TOKEN_ADOPTION);
};

export const isVsaAdoptionPortalChatSideBarEnabled = () => {
	return FeatureGates.checkGate(VSA_ADOPTION_CHAT_SIDE_BAR);
};

export const isHubsEnabledInHelpCenter = () => {
	return FeatureGates.checkGate(ENABLE_HUBS_IN_HELP_CENTER);
};

export const isMultiSelectEnabled = () => {
	return FeatureGates.checkGate(ENABLE_MULTI_SELECT_CF_UIM_SUPPORT);
};

export const isCortopv2ExperimentTGEnabled = () => {
	return FeatureGates.checkGate(CORTOPV2_EXPERIMENT_TG);
};

export const isCortopv2ExperimentEnabledWithExposure = (fireGateExposure = false) => {
	return FeatureGates.getExperimentValue(CORTOPV2_EXPERIMENT, 'isEnabled', false, {
		fireExperimentExposure: fireGateExposure,
	});
};

export const isJsmSurveysCoreFeatureEnabled = () => {
	return FeatureGates.checkGate(JSM_SURVEYS_CORE_FEATURE);
};

export const isSurveyResponseBugFixEnabled = () => {
	return FeatureGates.checkGate(JSM_SURVEYS_REMOVE_HIDDEN_SECTION_RESPONSES);
};

export const isJsmSurveysAdsBorderRadiusEnabled = () => {
	return FeatureGates.checkGate(JSM_SURVEYS_ADS_BORDER_RADIUS);
};

export const isForgeFixUndefinedExtensionErrorEnalbled = () => {
	return FeatureGates.checkGate(JSM_FORGE_PORTAL_FIX_UNDEFINED_EXTENSION_ERROR);
};

export const isJsmPortalRovoIntegrationEnabled = () => {
	return FeatureGates.checkGate(JSM_PORTAL_ROVO_INTEGRATION);
};

export const isDatePickerSupportEnabled = () => {
	return FeatureGates.checkGate(JSM_DATEPICKER_FIELD_SUPPORT);
};

export const isJsmPortalKeepRedirectionDestinationEnabled = () => {
	return FeatureGates.checkGate(JSM_PORTAL_KEEP_REDIRECTION_DESTINATION);
};

export const isRovoAgentsToolsConfigM2Enabled = () => {
	return FeatureGates.checkGate(ROVO_AGENTS_TOOLS_CONFIG_M2);
};

/**
 * Feature gate: rovo-service-agent-publish-on-create
 *
 * With agent versioning, a newly created Rovo service agent is a draft and is non-functional until
 * published. When on, the one-click create flow publishes the agent before connecting the widget,
 * and the agent selector lists only published editable agents.
 *
 * @rollout In progress
 * @owner Servo
 * @ticket NO-ISSUE
 */
export const ROVO_SERVICE_AGENT_PUBLISH_ON_CREATE = 'rovo-service-agent-publish-on-create';

export const isRovoServiceAgentPublishOnCreateEnabled = () => {
	return FeatureGates.checkGate(ROVO_SERVICE_AGENT_PUBLISH_ON_CREATE);
};

export const isStudioGsnAndScenarioRefreshEnabled = () => {
	return FeatureGates.checkGate('studio-gsn-and-scenario-refresh');
};

export const isRequestListInfiniteScrollEnabled = () => {
	return FeatureGates.checkGate(REQUEST_LIST_INFINITE_SCROLL);
};

export const isEmptyStringFilterOnFirstReqListLoadEnabled = () => {
	return FeatureGates.checkGate(EMPTY_STRING_FILTER_ON_FIRST_REQ_LIST_LOAD);
};

export const isApprovalListInfiniteScrollEnabled = () => {
	return FeatureGates.checkGate(APPROVAL_LIST_INFINITE_SCROLL);
};

const FOOTER_INLINE_SVG_ICON = 'jsd-footer-inline-svg-icon';

export const isFooterInlineSvgIconEnabled = () => FeatureGates.checkGate(FOOTER_INLINE_SVG_ICON);
